import CryptoJS from 'crypto-js'

import api from '@/api'
import {
  KEY_SALES_PLAN_ANNUAL_PERIOD,
  KEY_SALES_PLAN_DETAIL_ID,
  KEY_SALES_PLAN_MONTHLY_PERIOD
} from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'

const encryptedSalesPlanAnnualPeriod = btoa(KEY_SALES_PLAN_ANNUAL_PERIOD)
const encryptedSalesPlanMonthlyPeriod = btoa(KEY_SALES_PLAN_MONTHLY_PERIOD)
const encryptedDetailId = btoa(KEY_SALES_PLAN_DETAIL_ID)

const namespaced = true

const state = {
  revisionList: [],
  salesPlanPreview: {},
  salesPlanList: [],
  salesPlanDetail: [],
  salesPlanDetailId: localStorage.getItem(encryptedDetailId) || null,
  salesPlanAnnualPeriod: localStorage.getItem(encryptedSalesPlanMonthlyPeriod) || null,
  salesPlanAnnualLogs: [],
  salesPlanMonthlyPeriod: localStorage.getItem(encryptedSalesPlanAnnualPeriod) || null,
  salesPlanMonthlyLogs: []
}

const getters = {
  revisionList: state => state.revisionList,
  salesPlanPreview: state => state.salesPlanPreview,
  salesPlanList: state => state.salesPlanList,
  salesPlanDetail: state => state.salesPlanDetail,
  salesPlanDetailId: state => state.salesPlanDetailId
    ? Number(CryptoJS.AES.decrypt(state.salesPlanDetailId, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  salesPlanAnnualPeriod: state => state.salesPlanAnnualPeriod
    ? CryptoJS.AES.decrypt(state.salesPlanAnnualPeriod, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  salesPlanAnnualLogs: state => state.salesPlanAnnualLogs,
  salesPlanMonthlyPeriod: state => state.salesPlanMonthlyPeriod
    ? CryptoJS.AES.decrypt(state.salesPlanMonthlyPeriod, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  salesPlanMonthlyLogs: state => state.salesPlanMonthlyLogs
}

const mutations = {
  SET_REVISION_LIST: (state, payload) => {
    state.revisionList = payload
  },
  SET_SALES_PLAN_PREVIEW: (state, payload) => {
    state.salesPlanPreview = payload
  },
  SET_SALES_PLAN_LIST: (state, payload) => {
    state.salesPlanList = payload
  },
  SET_SALES_PLAN_DETAIL: (state, payload) => {
    state.salesPlanDetail = payload
  },
  SET_SALES_PLAN_DETAIL_ID: (state, payload) => {
    state.salesPlanDetailId = payload
    localStorage.setItem(encryptedDetailId, payload)
  },
  SET_SALES_PLAN_ANNUAL_PERIOD: (state, payload) => {
    state.salesPlanAnnualPeriod = payload
    localStorage.setItem(encryptedSalesPlanAnnualPeriod, payload)
  },
  SET_SALES_PLAN_ANNUAL_LOGS: (state, payload) => {
    state.salesPlanAnnualLogs = payload
  },
  SET_SALES_PLAN_MONTHLY_PERIOD: (state, payload) => {
    state.salesPlanMonthlyPeriod = payload
    localStorage.setItem(encryptedSalesPlanMonthlyPeriod, payload)
  },
  SET_SALES_PLAN_MONTHLY_LOGS: (state, payload) => {
    state.salesPlanMonthlyLogs = payload
  }
}

const actions = {
  async getRevisionList({ commit }) {
    try {
      const { data } = await api.getRevisionList()
      if (data.error) {
        commit('SET_REVISION_LIST', [])
        return
      }
      commit('SET_REVISION_LIST', data)
    } catch (error) {
      throw error
    }
  },
  async uploadExcel({ commit }, payload) {
    try {
      const formData = new FormData()
      formData.append('sales_plan_file', payload)

      const { data } = await api.uploadSalesPlan(formData, (progress) => {
        const totalLength = progress.lengthComputable
          ? progress.total
          : progress.target.getResponseHeader('content-length')
          || progress.target.getResponseHeader('x-decompressed-content-length')

        commit('UploadFile/SET_PROGRESS', totalLength
          ? Math.round((progress.loaded * 100) / totalLength)
          : 100,
        { root: true })
      })

      commit('SET_SALES_PLAN_PREVIEW', data ? data : {})
    } catch (error) {
      throw error
    }
  },
  async submitSalesPlan({ commit, dispatch }, payload) {
    try {
      await api.postSalesPlan(payload)
      commit('SET_SALES_PLAN_PREVIEW', {})
      dispatch('UploadFile/reset', { root: true })
    } catch (error) {
      throw error
    }
  },
  async getSalesPlanList({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage,
        limit: filterConfig.pageSize
      }
      const { data } = await api.getSalesPlanList({...filter, ...payload})

      if (data.error) {
        commit('SET_SALES_PLAN_LIST', {})
        commit('Table/SET_TOTAL_DATA', 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        return
      }

      commit('Table/SET_TOTAL_DATA', data.total, { root: true })
      commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
      commit('SET_SALES_PLAN_LIST', data.data)
    } catch (error) {
      throw error
    }
  },
  async getSalesPlanDetail({ commit }, payload) {
    try {
      const { data } = await api.getSalesPlan(payload)
      commit('SET_SALES_PLAN_DETAIL_ID', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_SALES_PLAN_DETAIL', [])
        return
      }
      commit('SET_SALES_PLAN_DETAIL', data.length ? data : [])
    } catch (error) {
      throw error
    }
  },
  async getSalesPlanAnnualLog({ commit }, payload) {
    try {
      const filter = {
        period: payload,
        type: 'annual',
        sortBy: 'created_at',
        sortDir: 'desc'
      }
      const { data } = await api.getSalesPlanList(filter)
      commit('SET_SALES_PLAN_ANNUAL_PERIOD', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_SALES_PLAN_ANNUAL_LOGS', [])
        return
      }
      commit('SET_SALES_PLAN_ANNUAL_LOGS', data)
    } catch (error) {
      throw error
    }
  },
  async getSalesPlanMonthlyLog({ commit }, payload) {
    try {
      const filter = {
        period: payload,
        sortBy: 'created_at',
        sortDir: 'desc'
      }
      const { data } = await api.getSalesPlanMonthlyLog(filter)
      commit('SET_SALES_PLAN_MONTHLY_PERIOD', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_SALES_PLAN_MONTHLY_LOGS', [])
        return
      }
      commit('SET_SALES_PLAN_MONTHLY_LOGS', data)
    } catch (error) {
      throw error
    }
  }
}

export default { namespaced, state, getters, mutations, actions }
